import { getEnvironmentTimeout, getApiUrl } from './client.js';

export class TripFinderService {
  constructor(apiClient, errorHandler) {
    this.apiClient = apiClient;
    this.handleApiError = errorHandler;
  }

  async buildTrip(params) {
    try {
      const timeout = getEnvironmentTimeout();
      const baseUrl = getApiUrl();

      const response = await this.apiClient.post('/tripfinder/build', params, {
        timeout,
        baseURL: baseUrl
      });

      return response.data;
    } catch (error) {
      console.error('Error building trip:', error);
      console.error('Error details:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        url: error.config?.url,
        method: error.config?.method,
      });
      throw this.handleApiError(error);
    }
  }

  async recommendDestinations(params) {
    try {
      const timeout = getEnvironmentTimeout();
      const baseUrl = getApiUrl();

      const response = await this.apiClient.post('/tripfinder/recommend', params, {
        timeout,
        baseURL: baseUrl
      });

      return response.data;
    } catch (error) {
      console.error('Error recommending destinations:', error);
      console.error('Error details:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        url: error.config?.url,
        method: error.config?.method,
      });
      throw this.handleApiError(error);
    }
  }

  async searchNearby(params) {
    try {
      const baseUrl = getApiUrl();

      const response = await this.apiClient.post('/tripfinder/nearby', params, {
        baseURL: baseUrl
      });
      return response.data;
    } catch (error) {
      console.error('Error searching nearby:', error);
      throw this.handleApiError(error);
    }
  }

  async healthCheck() {
    try {
      const baseUrl = getApiUrl();
      const response = await this.apiClient.get('/tripfinder/health', {
        baseURL: baseUrl
      });
      return response.data;
    } catch (error) {
      console.error('Error checking API health:', error);
      throw this.handleApiError(error);
    }
  }

  async getAvailableModes() {
    try {
      const baseUrl = getApiUrl();
      const response = await this.apiClient.get('/tripfinder/modes', {
        baseURL: baseUrl
      });
      return response.data;
    } catch (error) {
      console.error('Error getting available modes:', error);
      throw this.handleApiError(error);
    }
  }

  buildTripStream(params, { onStatus, onBatch, onComplete, onError }) {
    const baseUrl = getApiUrl();

    const url = `${baseUrl}/tripfinder/build/stream`;
    const eventSource = this._createSSEConnection(url, params);

    eventSource.addEventListener('status', (event) => {
      try {
        const data = JSON.parse(event.data);
        if (onStatus) onStatus(data);
      } catch (err) {
        console.error('Error parsing status event:', err);
      }
    });

    eventSource.addEventListener('batch', (event) => {
      try {
        const data = JSON.parse(event.data);
        if (onBatch) onBatch(data);
      } catch (err) {
        console.error('Error parsing batch event:', err);
      }
    });

    eventSource.addEventListener('complete', (event) => {
      try {
        const data = JSON.parse(event.data);
        if (onComplete) onComplete(data);
        eventSource.close();
      } catch (err) {
        console.error('Error parsing complete event:', err);
      }
    });

    eventSource.addEventListener('error', (event) => {
      try {
        const data = event.data ? JSON.parse(event.data) : { error: 'Unknown error' };
        console.error('Streaming error:', data);
        if (onError) onError(data);
        eventSource.close();
      } catch (err) {
        console.error('Error parsing error event:', err);
        if (onError) onError({ error: 'Stream connection failed' });
      }
    });

    eventSource.onerror = (err) => {
      console.error('EventSource error:', err);
      if (onError) onError({ error: 'Connection error' });
      eventSource.close();
    };

    return eventSource;
  }

  _createSSEConnection(url, params) {
    // Use fetch for POST requests with SSE
    const controller = new AbortController();
    let retryCount = 0;
    const maxRetries = 3;
    const retryDelay = 1000;
    let heartbeatTimer = null;
    const heartbeatTimeout = 30000; // 30s without data = stalled
    // Distinguishes a heartbeat-triggered abort from a user-initiated close().
    // Without this flag, the AbortError from the heartbeat is silently swallowed
    // and onError is never called, leaving the caller hanging indefinitely.
    let heartbeatAborted = false;

    // Return EventSource-like interface
    const eventSource = {
      _eventHandlers: {},
      readyState: 0, // 0=CONNECTING, 1=OPEN, 2=CLOSED
      addEventListener(type, handler) {
        if (!this._eventHandlers[type]) {
          this._eventHandlers[type] = [];
        }
        this._eventHandlers[type].push(handler);
      },
      close() {
        this.readyState = 2;
        controller.abort();
        if (heartbeatTimer) clearTimeout(heartbeatTimer);
      },
      onerror: null,
    };

    const resetHeartbeat = () => {
      if (heartbeatTimer) clearTimeout(heartbeatTimer);
      heartbeatTimer = setTimeout(() => {
        console.warn('SSE: Heartbeat timeout — no data received in 30s');
        heartbeatAborted = true;
        controller.abort();
      }, heartbeatTimeout);
    };

    const dispatchEvent = (type, data) => {
      const handlers = eventSource._eventHandlers[type];
      if (handlers) {
        const event = { type, data };
        handlers.forEach(handler => handler(event));
      }
    };

    const connect = async () => {
      if (eventSource.readyState === 2) return; // Closed

      try {
        const headers = {
          'Content-Type': 'application/json',
          'Accept': 'text/event-stream',
        };

        const response = await fetch(url, {
          method: 'POST',
          headers,
          body: JSON.stringify(params),
          credentials: 'include', // send httpOnly auth cookie cross-origin
          signal: controller.signal,
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        eventSource.readyState = 1;
        retryCount = 0; // Reset retries on successful connection
        resetHeartbeat();

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        // Keep event state across chunk boundaries (SSE event: and data: may arrive in different chunks)
        let eventType = 'message';
        let eventData = '';

        while (true) {
          const { done, value } = await reader.read();

          if (done) {
            break;
          }

          resetHeartbeat();
          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop(); // Keep incomplete line in buffer

          for (const line of lines) {
            const trimmedLine = line.trim();
            if (!trimmedLine) continue;

            if (line.startsWith('event:')) {
              eventType = line.slice(6).trim();
            } else if (line.startsWith('data:')) {
              eventData = line.slice(5).trim();

              if (eventData) {
                // Dispatch immediately
                dispatchEvent(eventType, eventData);

                // The 'complete' event listener is responsible for closing the connection.
                // After dispatching, we just reset for the next message.
                eventData = '';
                eventType = 'message';
              }
            } else if (line.startsWith(':')) {
              // Comment/ping - just keeps connection alive
              continue;
            }
          }
        }

      } catch (err) {
        if (err.name === 'AbortError' && !heartbeatAborted) {
          // User-initiated close() — exit silently
          return;
        }

        if (heartbeatAborted) {
          heartbeatAborted = false;
          console.warn('SSE: Heartbeat abort — treating as connection loss, entering retry');
        } else {
          console.error('SSE: Connection error:', err);
        }

        // Retry logic
        if (retryCount < maxRetries && eventSource.readyState !== 2) {
          retryCount++;
          const delay = retryDelay * Math.pow(2, retryCount - 1); // Exponential backoff

          // Notify error but don't close yet
          if (eventSource.onerror) {
            eventSource.onerror({ error: 'Connection lost, retrying...', retry: true });
          }

          setTimeout(() => connect(), delay);
        } else {
          console.error('SSE: Max retries reached or closed');
          eventSource.readyState = 2;
          if (eventSource.onerror) {
            eventSource.onerror({ error: 'Connection failed permanently' });
          }
          dispatchEvent('error', JSON.stringify({ error: 'Connection failed' }));
        }
      }
    };

    connect();

    return eventSource;
  }
}

export default TripFinderService;
